import { useState } from 'react';
import type {
  PortalCampaignRow,
  PortalCreativeRow,
  PortalCrmBlock,
  PortalLocationRow,
  PortalReportLabels,
  PortalSearchTermRow,
} from '../../../../types/portalReport';
import {
  crmConversionRateNote,
  crmCostPerSaleNote,
  crmCoverageNote,
  crmScopeNote,
} from '../../../../lib/portalReport/reportCopy';
import { formatReportingAmount } from '../../../../lib/reportingMoney';

// CLIENT PORTAL Phase 1 — the report's tables. Plain kyck styling; per-brand
// theming is Phase 2.
//
// ⚠️ EVERY HEADING READS ITS WORDING FROM `labels`, never a hardcoded "Leads".
// A gym counts members and a dealership counts sales; the CRM phase supplies
// those words through the per-brand config, and it can only do that if nothing
// here has baked one in.

const num = (n: number): string => n.toLocaleString('en-US', { maximumFractionDigits: 1 });
const money = (n: number, c: string): string => formatReportingAmount(n, c);
/** ⚠️ Null CPL renders as an em dash, never as $0 — see deriveCostPerLead. */
const cpl = (n: number | null, c: string): string => (n == null ? '—' : money(n, c));

function Table({
  head,
  children,
  caption,
}: {
  head: React.ReactNode;
  children: React.ReactNode;
  caption?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <thead className="bg-background text-left text-xs uppercase tracking-wide text-muted-foreground">
          {head}
        </thead>
        <tbody className="divide-y divide-border">{children}</tbody>
      </table>
      {caption && <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">{caption}</p>}
    </div>
  );
}

const TH = 'px-4 py-2.5 font-medium';
const TD = 'px-4 py-2.5 text-foreground';
const TDNUM = 'px-4 py-2.5 text-right tabular-nums text-foreground';
const THNUM = 'px-4 py-2.5 text-right font-medium';

export function CampaignsTable({
  rows,
  labels,
  title = 'Campaigns',
}: {
  rows: PortalCampaignRow[];
  labels: PortalReportLabels;
  title?: string;
}) {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-foreground">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No campaigns ran in this period.</p>
      ) : (
        <Table
          head={
            <tr>
              <th className={TH}>Campaign</th>
              <th className={THNUM}>{labels.spend}</th>
              <th className={THNUM}>{labels.leads}</th>
              <th className={THNUM}>{labels.costPerLead}</th>
              <th className={THNUM}>Impressions</th>
              <th className={THNUM}>Clicks</th>
            </tr>
          }
        >
          {rows.map((r) => (
            <tr key={r.campaignId} className="hover:bg-background">
              <td className={TD}>{r.campaignName}</td>
              <td className={TDNUM}>{money(r.spend, r.currency)}</td>
              <td className={TDNUM}>{num(r.leads)}</td>
              <td className={TDNUM}>{cpl(r.costPerLead, r.currency)}</td>
              <td className={TDNUM}>{r.impressions == null ? '—' : num(r.impressions)}</td>
              <td className={TDNUM}>{r.clicks == null ? '—' : num(r.clicks)}</td>
            </tr>
          ))}
        </Table>
      )}
    </section>
  );
}

export function ByLocationTable({
  rows,
  labels,
  onSelect,
}: {
  rows: PortalLocationRow[];
  labels: PortalReportLabels;
  /** Drilling into a location re-scopes the page — server-side, narrowing only. */
  onSelect?: (accountId: string) => void;
}) {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-foreground">By location</h3>
      <Table
        head={
          <tr>
            <th className={TH}>Location</th>
            <th className={THNUM}>{labels.spend}</th>
            <th className={THNUM}>{labels.leads}</th>
            <th className={THNUM}>{labels.costPerLead}</th>
          </tr>
        }
      >
        {rows.map((r) => (
          <tr key={r.accountId} className="hover:bg-background">
            <td className={TD}>
              {onSelect ? (
                <button
                  type="button"
                  className="text-left font-medium text-foreground underline decoration-border underline-offset-2 hover:decoration-current"
                  onClick={() => onSelect(r.accountId)}
                >
                  {r.accountName}
                </button>
              ) : (
                r.accountName
              )}
            </td>
            {/*
              ⚠️ A CLUB THE PERIOD DID NOT MEASURE RENDERS DASHES, NOT ZEROS
              (2026-09-03). `PortalLocationRow` now carries each club's OWN
              coverage, and every figure is null when it is `no-data` — a 0 on a
              per-club table reads as "this club produced nothing", which is a
              different and, on a franchise report, commercially damaging claim.
              ⚠️ This is the PRE-RESTYLE path, kept as the revert path; it gets
              the honest render, not the new columns.
            */}
            <td className={TDNUM}>
              {r.spend === null || r.currency === null ? '—' : money(r.spend, r.currency)}
            </td>
            <td className={TDNUM}>{r.leads === null ? '—' : num(r.leads)}</td>
            <td className={TDNUM}>
              {r.currency === null ? '—' : cpl(r.costPerLead, r.currency)}
            </td>
          </tr>
        ))}
      </Table>
    </section>
  );
}

export function SearchTermsTable({
  rows,
  labels,
  capped,
  total,
}: {
  rows: PortalSearchTermRow[];
  labels: PortalReportLabels;
  capped: boolean;
  total: number;
}) {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-foreground">What people searched for</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No search terms recorded in this period.</p>
      ) : (
        <Table
          caption={[
            // ⚠️ The true total, never a truncated list presented as complete.
            capped ? `Showing the top ${rows.length} of ${total.toLocaleString()} search terms.` : null,
            // ⚠️ Google reports these at ad-group grain and above. Saying so
            // stops a client inferring a per-ad precision that does not exist.
            'Google reports search terms by ad group, so these cover groups of ads rather than individual ads.',
          ]
            .filter(Boolean)
            .join(' ')}
          head={
            <tr>
              <th className={TH}>Search term</th>
              <th className={THNUM}>Clicks</th>
              <th className={THNUM}>{labels.leads}</th>
              <th className={THNUM}>{labels.spend}</th>
              <th className={THNUM}>{labels.costPerLead}</th>
            </tr>
          }
        >
          {rows.map((r) => (
            <tr key={`${r.searchTerm}-${r.matchType ?? ''}`} className="hover:bg-background">
              <td className={TD}>{r.searchTerm}</td>
              <td className={TDNUM}>{num(r.clicks)}</td>
              <td className={TDNUM}>{num(r.leads)}</td>
              <td className={TDNUM}>{money(r.spend, r.currency)}</td>
              <td className={TDNUM}>{cpl(r.costPerLead, r.currency)}</td>
            </tr>
          ))}
        </Table>
      )}
    </section>
  );
}

export function CreativesGrid({
  rows,
  labels,
}: {
  rows: PortalCreativeRow[];
  labels: PortalReportLabels;
}) {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-foreground">Ads that ran</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No ads ran in this period.</p>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <li
              key={r.adId}
              className="overflow-hidden rounded-lg border border-border bg-card"
            >
              <CreativeThumbnail url={r.thumbnailUrl} name={r.adName} />
              <div className="p-3">
                <div className="truncate text-sm font-medium text-foreground" title={r.adName ?? ''}>
                  {r.adName ?? 'Untitled ad'}
                </div>
                {r.campaignName && (
                  <div className="truncate text-xs text-muted-foreground" title={r.campaignName}>
                    {r.campaignName}
                  </div>
                )}
                <dl className="mt-2 grid grid-cols-3 gap-1 text-xs">
                  <Metric label={labels.spend} value={money(r.spend, r.currency)} />
                  <Metric label={labels.leads} value={num(r.leads)} />
                  <Metric label={labels.costPerLead} value={cpl(r.costPerLead, r.currency)} />
                </dl>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="truncate text-muted-foreground" title={label}>
        {label}
      </dt>
      <dd className="font-medium tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

/**
 * ⚠️ A MISSING OR BROKEN THUMBNAIL RENDERS A PLACEHOLDER, NEVER A BROKEN IMAGE.
 * Nulls are normal (creatives pulled before the thumbnail dimension existed, a
 * video with no still), and the URLs are signed CDN links that legitimately
 * expire — so the `onError` fallback matters as much as the null check.
 */
function CreativeThumbnail({ url, name }: { url: string | null; name: string | null }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) {
    return (
      <div className="flex aspect-square w-full items-center justify-center bg-muted text-xs text-muted-foreground">
        No preview
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={name ?? 'Ad creative'}
      loading="lazy"
      onError={() => setFailed(true)}
      className="aspect-square w-full bg-muted object-cover"
    />
  );
}

/**
 * The club's own leads and sales, from the client's CRM — plain-styled, for the
 * pre-restyle report.
 *
 * ⚠️⚠️ THE SAME PURE COPY AS THE RESTYLE'S PANEL, imported rather than restated.
 * Two reports carrying two versions of "conversion rate is club-level, not an ad
 * rate" is two chances for one of them to be softened, and the caveat is the
 * thing that makes these figures publishable at all. Only the markup differs.
 *
 * ⚠️ The caveats LEAD the figures. A caveat read after a number has been
 * believed is not a caveat.
 */
export function CrmSection({
  crm,
  labels,
  range,
}: {
  crm: PortalCrmBlock;
  labels: PortalReportLabels;
  range: { from: string; to: string };
}) {
  const coverage = crmCoverageNote(range, crm.coverage, (iso) => iso);
  const scope = crmScopeNote(crm.countedClubs.length, crm.uncountedClubs);
  // ⚠️ A period the CRM does not cover suppresses the FIGURES, not just the tone
  // — a reader takes the number over the sentence every time.
  const unmeasured = crm.coverage.firstDate === null || range.to < crm.coverage.firstDate;

  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-foreground">Leads and sales at the club</h3>
      {coverage && <p className="mb-2 text-xs text-muted-foreground">{coverage}</p>}
      {scope && <p className="mb-2 text-xs text-muted-foreground">{scope}</p>}
      {crm.leadBasis && <p className="mb-2 text-xs text-muted-foreground">{crm.leadBasis.note}</p>}
      {!unmeasured && (
        <>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {/* ⚠️ A monthly-grain source with no whole uploaded month has NULL leads: a dash, never 0. */}
            <CrmFigure
              label={`${labels.leads} (CRM)`}
              value={crm.totals.leads === null ? '—' : num(crm.totals.leads)}
            />
            <CrmFigure
              label="Digital leads"
              value={crm.totals.digitalLeads === null ? '—' : num(crm.totals.digitalLeads)}
            />
            <CrmFigure label={labels.sales} value={num(crm.totals.sales)} />
            <CrmFigure
              label={labels.conversionRate}
              // ⚠️ Null is an em dash, never 0% — no enquiries means nothing
              // converted OR failed to.
              value={
                crm.totals.conversionRate === null ? '—' : `${crm.totals.conversionRate.toFixed(1)}%`
              }
            />
          </dl>
          <p className="mt-2 text-xs text-muted-foreground">{crmConversionRateNote(labels)}</p>
          {crm.costPerSale.length > 0 && (
            <>
              {/* ⚠️⚠️ The caveat ships with the metric or the metric does not ship. */}
              <p className="mt-3 text-xs text-muted-foreground">{crmCostPerSaleNote(labels)}</p>
              <dl className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {crm.costPerSale.map((c) => (
                  <CrmFigure
                    key={c.currency}
                    label={
                      crm.costPerSale.length > 1
                        ? `${labels.costPerSale} (${c.currency})`
                        : labels.costPerSale
                    }
                    // ⚠️ Spend with no sales is a dash, never $0.00.
                    value={c.costPerSale === null ? '—' : money(c.costPerSale, c.currency)}
                  />
                ))}
              </dl>
            </>
          )}
        </>
      )}
    </section>
  );
}

function CrmFigure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}
